import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Typography,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { publicShareApi } from '../../services/fileShareApi';

const PublicShareDialog = ({ open, onClose, file }) => {
  const [expiryDuration, setExpiryDuration] = useState('7d');
  const [loading, setLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [error, setError] = useState(null);
  const [copySuccess, setCopySuccess] = useState('');

  // Debug: Log when dialog opens and file changes
  React.useEffect(() => {
    if (open) {
      console.log('🔍 PublicShareDialog opened with file:', file);
      console.log('🔍 File ID:', file?.id || file?.fileId);
      console.log('🔍 File name:', file?.name || file?.fileName);
    }
  }, [open, file]);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError(null);

      const payload = {
        fileId: file.id || file.fileId,
        expiryDuration,
        allowDownload: false,
      };

      console.log('🔍 Generating share link with payload:', payload);

      const response = await publicShareApi.generateShareLink(
        payload.fileId,
        payload.expiryDuration,
        undefined,
        payload.allowDownload
      );

      console.log('🔍 Share link response:', response);

      if (response.data.success) {
        const { shareUrl } = response.data.data;
        const fullUrl = `${window.location.origin}${shareUrl}`;
        setGeneratedLink(fullUrl);
      } else {
        setError(response.data.message || 'Failed to generate share link');
      }
    } catch (err) {
      console.error('Error generating share link:', err);
      setError(err.response?.data?.message || 'Failed to generate share link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(`${type} copied to clipboard!`);
    setTimeout(() => setCopySuccess(''), 3000);
  };

  // Responsive iframe that fills the embed container area.
  const embedCode = generatedLink
    ? `<iframe src="${generatedLink}" style="width:100%;height:100%;border:0;" allow="fullscreen" title="File viewer"></iframe>`
    : '';

  const handleClose = () => {
    setGeneratedLink(null);
    setError(null);
    setExpiryDuration('7d');
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <ShareIcon />
              <Typography variant="h6">Share File Publicly</Typography>
            </Box>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {!generatedLink ? (
            <Box display="flex" flexDirection="column" gap={3}>
              {/* File Info */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  File
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {file?.name || file?.fileName || 'Unknown'}
                </Typography>
              </Box>

              {/* Expiry Type — duration presets only (custom date/time removed) */}
              <FormControl fullWidth>
                <InputLabel>Expiry Type</InputLabel>
                <Select value="duration" label="Expiry Type" onChange={() => {}}>
                  <MenuItem value="duration">Duration Preset</MenuItem>
                </Select>
              </FormControl>

              {/* Duration Presets */}
              <FormControl fullWidth>
                <InputLabel>Expires In</InputLabel>
                <Select
                  value={expiryDuration}
                  label="Expires In"
                  onChange={(e) => setExpiryDuration(e.target.value)}
                >
                  <MenuItem value="1h">1 Hour</MenuItem>
                  <MenuItem value="6h">6 Hours</MenuItem>
                  <MenuItem value="24h">24 Hours</MenuItem>
                  <MenuItem value="7d">7 Days</MenuItem>
                  <MenuItem value="30d">30 Days</MenuItem>
                  <MenuItem value="never">No expiry (link works for lifetime)</MenuItem>
                </Select>
              </FormControl>

              {/* Info Alert */}
              <Alert severity="info">
                Anyone with the link can view the file (no password required). You can paste or embed this link anywhere.
              </Alert>

              {/* Error Display */}
              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={3}>
              {/* Success Message */}
              <Alert severity="success">
                Public share link generated successfully!
                {expiryDuration === 'never'
                  ? ' (No expiry)'
                  : expiryDuration === '30d'
                    ? ' (Expires in 30 Days)'
                    : expiryDuration === '7d'
                      ? ' (Expires in 7 Days)'
                      : expiryDuration === '24h'
                        ? ' (Expires in 24 Hours)'
                        : expiryDuration === '6h'
                          ? ' (Expires in 6 Hours)'
                          : expiryDuration === '1h'
                            ? ' (Expires in 1 Hour)'
                            : ''}
              </Alert>

              {/* Share / Embed Link */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Share or embed link
                </Typography>
                <Box display="flex" gap={1}>
                  <TextField
                    fullWidth
                    value={generatedLink}
                    InputProps={{ readOnly: true }}
                    size="small"
                  />
                  <IconButton
                    color="primary"
                    onClick={() => handleCopy(generatedLink, 'Link')}
                  >
                    <CopyIcon />
                  </IconButton>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Paste this URL in a browser or embed it in an iframe. No password required.
                </Typography>
              </Box>

              {/* Embed in your application */}
              <Box>
                <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                  Embed in your application
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Use the URL below in an iframe. Anyone with the link can view the file (no password).
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Embed URL
                </Typography>
                <Box display="flex" gap={1} sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={generatedLink || ''}
                    InputProps={{ readOnly: true }}
                  />
                  <IconButton
                    color="primary"
                    onClick={() => handleCopy(generatedLink, 'Embed URL')}
                    title="Copy embed URL"
                  >
                    <CopyIcon />
                  </IconButton>
                </Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Example iframe code
                </Typography>
                <Box display="flex" gap={1} alignItems="flex-start">
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    maxRows={3}
                    value={embedCode}
                    InputProps={{
                      readOnly: true,
                      sx: { fontFamily: 'monospace', fontSize: '0.8rem' },
                    }}
                  />
                  <IconButton
                    color="primary"
                    onClick={() => handleCopy(embedCode, 'Embed code')}
                    title="Copy embed code"
                  >
                    <CopyIcon />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {!generatedLink ? (
            <>
              <Button onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <ShareIcon />}
              >
                {loading ? 'Generating...' : 'Generate Link'}
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={handleClose}>
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={!!copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess('')}
        message={copySuccess}
      />
    </>
  );
};

export default PublicShareDialog;

